import type { LocaleMessageSchema } from '../types';

export default {
  Common: {
    loading: 'Laden...',
  },
  errors: {
    session_create_error: 'Kon sessie niet aanmaken.\nProbeer het opnieuw.',
    session_continue_error:
      'We konden je sessie niet laden. Probeer het opnieuw. Als het probleem blijft, neem contact op met support via {supportEmail}.',
    kiosk_not_found: 'Kon kiosk niet laden.\nProbeer het opnieuw.',

    art_style_missing: 'Er zijn geen kunststijlen\ntoegevoegd aan deze kiosk',
    art_style_fetch_error: 'Kon kunststijl niet laden',
    art_style_preload_error: 'Kon preview van kunststijl niet laden',

    face_detection_server_error: 'Foto verwerken mislukt. Probeer het opnieuw.',
    face_detection_preload_error:
      'Fout bij het vooraf laden van gedetecteerde gezichten',
    face_detection_no_faces:
      'Geen gezichten gedetecteerd. Probeer een andere foto te maken.',

    photo_webcam_not_allowed: 'Toestemming voor webcam geweigerd',
    photo_webcam_not_found:
      'Webcam niet gevonden. Controleer of deze is aangesloten op de kiosk.',
    photo_webcam_hardware_error:
      'Webcam is bezet of werkt niet.\nProbeer het opnieuw of vraag om hulp.',
    photo_webcam_photo_failed: 'Kon geen foto maken\nmet de webcam',

    photo_phone_missing_session_id:
      'We konden je sessie niet vinden.\nScan de QR-code opnieuw.',
    photo_phone_missing_kiosk_slug:
      'Kiosk niet gevonden in je sessie. Als het probleem aanhoudt, neem contact op met support via {supportEmail}.',
    photo_phone_limit_exceeded: "Te veel foto's geselecteerd",
    photo_phone_qr_error: 'Kon QR-code niet genereren\nvoor foto-upload',

    photo_file_unsupported_type: 'Niet-ondersteund bestandstype',
    photo_file_cannot_process:
      'Kon deze foto niet verwerken.\nProbeer een andere.',
    photo_file_cannot_process_heic:
      'Kon HEIC-foto niet verwerken.\nProbeer een andere.',

    portrait_generation_error:
      'Er is een fout opgetreden bij het genereren van je portret. Probeer het opnieuw.',
    portrait_generation_faces_not_selected:
      'Geen gezichten geselecteerd voor\ngenereren van portret',
    portrait_generation_style_not_selected:
      'Geen kunststijl geselecteerd voor\ngenereren van portret',
    portrait_generation_over_limit: 'Te veel pogingen gedaan',

    portraits_download_session_error:
      'We konden je sessie niet vinden. Controleer de ingevoerde code. Als het probleem aanhoudt, neem contact op met support via {supportEmail}.',
    portraits_download_kiosk_error:
      'Kiosk niet gevonden in je sessie. Als het probleem aanhoudt, neem contact op met support via {supportEmail}.',
    portraits_download_portraits_error:
      'Geen portretten gevonden in je sessie. Als het probleem aanhoudt, neem contact op met support via {supportEmail}.',

    payment_partner_mode_missing_stripe_account:
      'Partner-betalingsmodus vereist een Stripe Connect-account. Configureer het partner Stripe-account of schakel over naar platform-betalingsmodus.',

    api_error_missing_session_id: 'Sessie niet gevonden',
    api_error_email_code_validation:
      'Betalingscode valideren mislukt. Probeer het opnieuw.',
  },

  /**
   * Global components
   */

  OverlayError: {
    buttons: {
      close: 'Sluiten',
      restart: 'Opnieuw starten',
      retry: 'Opnieuw proberen',
    },
  },
  Logo: {
    startOver: 'Opnieuw starten',
  },
  Copyright: {
    copyright: 'powered by TheArtisan.ai',
    distribution: 'verdeeld door',
    terms:
      'Door een foto te maken op dit apparaat, accepteer je onze beleidsregels en voorwaarden. Om ze te bekijken, klik',
    termsLink: 'hier',
  },
  IdlePopup: {
    title: 'Ben je nog hier?',
    caption: 'Je sessie eindigt als je inactief bent.',
    buttonContinue: 'Ja, ik ben nog hier',
    buttonRestart: 'Nee, opnieuw starten',
  },
  GDPRConsent: {
    title: 'Toestemming vereist',
    consentText:
      'We hebben uw toestemming nodig om uw gezichtskenmerken (biometrische gegevens) te verwerken om artistieke transformaties te creëren. U kunt alleen doorgaan als u 16 jaar of ouder bent (of ouderlijke toestemming hebt).',
    buttonConsent: 'Ik ga akkoord',
    buttonDecline: 'Ik ga niet akkoord',
    privacyPolicyLink: 'Bekijk privacybeleid',
    declineMessage: 'U kunt niet doorgaan zonder toestemming',
  },
  StepProgressIndicator: {
    photoShort: 'Foto',
    photoLong: 'Maak foto',
    facesShort: 'Gezichten',
    facesLong: 'Kies gezichten',
    groupPortraitsShort: 'Groep',
    groupPortraitsLong: 'Groepsportretten',
    portraitsShort: 'Portretten',
    portraitsLong: 'Kies portretten',
    printShort: 'Print',
    printLong: 'Betaal & Print',
    sendToPickupShort: 'Ophalen',
    sendToPickupLong: 'Naar ophaalpunt',
  },
  UploadedPhotosSidebar: {
    title: "Je foto's",
    count: '+{extraPhotoCount} meer',
  },
  NumericKeyboard: {
    invalidPin: 'Ongeldige PIN-code',
    clear: 'Wissen',
    enter: 'Invoeren',
  },
  OutOfService: {
    message:
      'Uw contract lijkt gepauzeerd of verlopen te zijn.\nNeem contact op met uw distributeur voor ondersteuning.',
  },

  /**
   * Steps
   */

  Home: {
    title: 'Genereer je portret in seconden!',
    button: 'Probeer nu!',
  },
  ArtStyle: {
    title: 'Kies je stijl',
  },
  PhotoFromWebcam: {
    titleGetReady: 'Maak je klaar om een foto te maken',
    titleAttention: 'Kijk in de camera!',
    titleReview: 'Vind je deze foto leuk?',
    titleUpload: 'Foto wordt geüpload…',
    buttonPrimaryPhoto: 'Maak foto',
    buttonPrimaryUse: 'Ja, gebruik deze foto',
    buttonPrimaryRetake: 'Opnieuw foto maken',
    buttonSecondaryUpload: 'Upload vanaf telefoon',
    buttonSecondaryRetake: 'Nee, opnieuw (1 poging over)',
    buttonSecondaryUseFirst: 'Nee, gebruik de eerste foto',
    titleCameraError: 'Camera niet beschikbaar',
    buttonRetryCamera: 'Camera opnieuw proberen',
  },
  PhotoPromptFromPhone: {
    title: "Foto's uploaden vanaf telefoon?",
    caption:
      "Verras vrienden en familie —\nupload hun foto's om portretten te maken.",
    buttonUpload: 'Ja, upload vanaf telefoon',
    buttonSkip: 'Nee, sla telefoon-upload over',
  },
  PhotoFromPhone: {
    title: "Upload foto's vanaf je telefoon",
    step1: 'Scan de QR-code met je telefoon',
    step2: "Selecteer foto's",
    step3: 'Wacht op de upload',
    waiting: 'Wachten op upload…',
    done: 'Upload succesvol',
    buttonCancel: 'Annuleer & ga terug',
    buttonSkip: 'Sla telefoon-upload over',
    buttonContinue: 'Doorgaan',
    gdprConsentAll:
      'Ik heb toestemming van alle personen op deze foto om hun gezichtskenmerken te verwerken',
    gdprOver16: 'Niemand op deze foto is jonger dan 16 jaar',
    gdprResponsibility:
      'Ik accepteer de volledige verantwoordelijkheid voor AVG-naleving',
    gdprAgreeToPrefix: 'Ik ga akkoord met de',
    gdprPrivacyPolicyLink: 'privacybeleid voorwaarden',
    gdprAgreeToSuffix: "voor geüploade foto's",
    gdprCheckboxesRequired: 'Vink alle vakjes hieronder aan om door te gaan',
    buttonSkipKioskPhoto: 'Kiosk-foto overslaan',
    buttonTakeWebcamPhoto: 'Foto maken met webcam',
  },
  FacesIndividual: {
    title: 'Van wie wil je individuele portretten maken?',
    buttonConfirm: 'Bevestigen & doorgaan',
    genderMale: 'Man',
    genderFemale: 'Vrouw',
    genderNonBinary: 'Non-Binair',
    age0to2: '0-2',
    age3to9: '3-9',
    age10to19: '10-19',
    age20to29: '20-29',
    age30to39: '30-39',
    age40to49: '40-49',
    age50to59: '50-59',
    age60to69: '60-69',
    age70plus: '70+',
    raceWhite: 'Wit',
    raceBlack: 'Zwart',
    raceEastAsian: 'Oost-Aziatisch',
    raceSoutheastAsian: 'Zuidoost-Aziatisch',
    raceIndian: 'Indiaas',
    raceLatino: 'Latino',
    raceMena: 'Midden-Oosters',
  },
  Portraits: {
    title: 'Kies je portretten!',
    buttonNext: 'Volgende: geselecteerde printen',
    buttonNextGroupOnly: 'Alleen doorgaan met groepsportretten',
    galleryScrollBack: 'Vorige portretten',
    galleryScrollForward: 'Volgende portretten',
    filterLabel: 'Tonen',
    filterAll: 'Alle',
    filterGroup: 'Samen',
    filterIndividual: 'Alleen',
    stageKindGroup: 'Groepsportret',
    stageKindIndividual: 'Portret',
    stagePosition: '{index} van {count}',
    stageSelect: 'Kiezen',
    stageSelected: 'Gekozen ✓',
    buttonMore: 'Maak meer portretten ({count} over)',
    popupTitle: 'Wil je je bestelling afronden?',
    popupGroupSelected: 'Groepsportretten geselecteerd: {count} ({price})',
    popupIndividualSelected:
      'Individuele portretten geselecteerd: {count} ({price})',
    popupTotalSum: 'Totaal: {sum}',
    popupButtonNo: 'Nee, ga terug',
    popupButtonYes: 'Ja, print & betaal!',
    error: 'Oeps! Kon dit portret niet genereren.',
    placeholderGenerating: 'Portret wordt gegenereerd…',
    placeholderLoading: 'Portret wordt geladen…',
    groupPlaceholder: 'Hier verschijnt je groepsportret',
    buttonRetry: 'Opnieuw proberen',
    buttonErrorDetails: 'Foutdetails',
    retrying: 'Opnieuw proberen...',
    maxRetriesReached: 'Max. pogingen bereikt',
    retryAttempts: 'Pogingen: {count}',
    printingLockedPopupTitle:
      'Roep het dichtstbijzijnde personeelslid om je portretten te printen.',
    generationPendingTitle: 'Sommige portretten worden nog gemaakt',
    generationPendingCaption:
      '{count} portretten worden nog gemaakt. Wil je wachten om ze allemaal te zien?',
    generationPendingWait: 'Wacht op alle portretten',
    generationPendingSendReady: 'Stuur {count} portretten die klaar zijn',
    printingLockedPopupCaption:
      'Printen bij de kiosk is niet beschikbaar als er geen personeelslid aanwezig is.',
  },
  PortraitsGroup: {
    buttonMore: 'Maak meer portretten',
  },
  PrintLock: {
    lockTitle: 'Printen vergrendelen',
    unlockTitle: 'Printen ontgrendelen',
  },
  PaymentType: {
    title: 'Hoe wil je betalen?',
    optionCardTitle: 'Creditcard',
    optionCardCaption: 'Visa, Mastercard, & 10+ ondersteunde kaarten',
    optionVoucherTitle: 'Vooruitbetaalde Portretten',
    optionVoucherCaption: 'Inbegrepen in ticket — vraag het personeel',
  },
  PaymentTerminal: {
    title: 'Volg de instructies op het betaalterminal',
    titlePaymentSuccess: 'Je portretten worden geprint…',
    titlePaymentError: 'Betaling mislukt',
    titlePrintError1: 'Sorry, sommige portretten konden niet geprint worden',
    titlePrintError2: 'Klik op "Opnieuw printen" om het opnieuw te proberen.',
    buttonRetryPrinting: 'Opnieuw printen',
    buttonRetry: 'Opnieuw proberen',
    buttonCancel: 'Annuleren',
    buttonExitSession: 'Opnieuw beginnen',
    buttonBack: 'Terug',
    total: 'Totaal:',
    captionPayment: 'Wachten op je betaling…',
    captionPrinting: 'Printen succesvol',
    captionPaymentError:
      'Er is iets misgegaan. Probeer opnieuw of begin opnieuw.',
    captionPaymentFinal:
      'De betaling is niet gelukt. Vraag een medewerker om hulp.',
    failureCaptions: {
      notEnoughFunds:
        'Er staat niet genoeg saldo op de kaart. Probeer een andere kaart.',
      expiredCard: 'Deze kaart is verlopen. Probeer een andere kaart.',
      cardDeclined: 'De kaart is geweigerd. Probeer een andere kaart.',
      cardError:
        'Deze kaart kan hier niet worden gebruikt. Probeer een andere kaart.',
      processingError:
        'De betaling is niet gelukt. Probeer het opnieuw met dezelfde kaart.',
      readerError:
        'De pinautomaat is niet beschikbaar. Vraag een medewerker om hulp.',
      timeout: 'De betaling duurde te lang. Probeer het opnieuw.',
    },
    errorMessage: 'Sorry, we konden je betaling niet verwerken',
    popupCancelledTitle: 'Betaling geannuleerd',
    popupCancelledCaption: 'Wil je opnieuw proberen of opnieuw beginnen?',
    popupButtonContinue: 'Opnieuw proberen',
  },
  PaymentVoucher: {
    title:
      'Neem contact op met een medewerker voor hulp bij het betalen met een voucher',
    buttonBack: 'Terug',
    pinTitle: 'Betalen met voucher',
  },
  Printing: {
    title: 'Je portretten worden geprint…',
    titleError: 'Sorry, sommige portretten konden niet geprint worden',
    buttonPrintAgain: 'Opnieuw printen',
    buttonContinueWithoutPrint: 'Doorgaan zonder printen',
    error: 'Oeps! Kon dit portret niet printen.',
    errorPartial: '{printed} van {total} geprint',
    retrying: 'Nieuwe poging…',
    waiting: 'Wachten op de printer…',
  },
  PrintFailed: {
    title: 'Sorry, we konden je foto’s niet printen',
    titlePartial: '{printed} van {total} foto’s geprint',
    caption: 'Je kunt een nieuwe sessie starten en nieuwe portretten maken.',
    captionRefunded: 'We hebben {amount} teruggestort op je kaart.',
    captionNotCharged: 'We hebben {amount} niet in rekening gebracht.',
    captionRefundHelp: 'Een medewerker helpt je met de terugbetaling.',
  },
  Success: {
    title: 'Dank je!',
    caption: 'Je schilderijen zijn geprint.\nHaal ze op bij de receptie.',
    buttonStartAgain: 'Opnieuw starten',
  },
  GoPhotoScan: {
    title: 'Scan je PhotoPass',
    subtitle: 'Houd je PhotoPass QR-code voor de scanner',
    invalidCode: 'Ongeldig code formaat. Probeer opnieuw te scannen.',
    failedToRegister: 'Code registreren mislukt. Probeer het opnieuw.',
    error: 'Er is een fout opgetreden. Probeer het opnieuw.',
  },

  /**
   * Customer-facing components
   */

  CustomerPhotoFromPhone: {
    title: "Upload foto's vanuit je galerij",
    buttonSelect: "Selecteer foto's",
    maxCount: "Maximaal {count} foto's",
    uploading: 'Uploaden…',
    uploadError:
      'Upload mislukt. Controleer je verbinding en probeer het opnieuw.',
    buttonRetry: 'Opnieuw proberen',
    successTitle: 'Upload succesvol',
    successCaption:
      'Ga terug naar de kiosk en volg de instructies op het scherm.',
  },
  CustomerPortraitsDownload: {
    title: 'Voer code in om je portret te krijgen',
    buttonSubmit: 'Verzenden',
    error:
      'Deze code lijkt niet te kloppen. Controleer de code van 8 tekens op je foto en probeer het opnieuw.',
    errors: {
      wrongFormat:
        'Deze code lijkt niet te kloppen. Controleer de code van 8 tekens op je foto en probeer het opnieuw.',
      notFound:
        'Sorry, geen portretten gevonden. Controleer de code — deze staat op de voorkant van het portret linksonder gedrukt.',
      serverError:
        'Sorry, we konden je portretten op dit moment niet ophalen. Probeer het later opnieuw.',
    },
  },
  CustomerSessionPass: {
    tabs: {
      photos: 'Foto’s',
      pass: 'QR-pas',
    },
    eyebrow: 'Jouw sessiepas',
    title: 'Houd deze pagina open',
    description:
      'Scan bij het verkooppunt de vierkante code hieronder. Je portretten zijn beschikbaar op het tabblad Foto’s.',
    descriptionNothingPrinted:
      'Scan bij het verkooppunt de vierkante code hieronder om je foto’s te kiezen en te printen.',
    scanLabel: 'Scan bij het verkooppunt',
    codeLabel: 'Sessiecode',
    ready: 'Klaar om te scannen',
    loading: 'Code voorbereiden',
    unavailable: 'Code niet beschikbaar',
    qrAlt: 'QR-code voor het verkooppunt',
    defaultVenue: 'TheArtisan.ai-portretervaring',
  },
  CustomerPortraitsDownloadCode: {
    error: 'Oeps! Kon dit portret niet laden.',
    placeholder: 'Portret wordt geladen…',
    imageAlt: 'Portret',
    buttonDownload: 'Downloaden',
    buttonSendOrShare: 'Verzenden of delen',
    buttonRetry: 'Opnieuw proberen',
    shareTitle: 'Je portret van {clientName}',
    shareText: 'Gemaakt door TheArtisan.ai',
    states: {
      processing:
        'Je portretten worden nog voorbereid. Probeer het zo opnieuw.',
      partial:
        'Sommige portretten of paginadetails konden niet worden geladen. Beschikbare downloads blijven werken.',
    },
    actions: {
      enterAnotherCode: 'Andere code invoeren',
      contactSupport: 'Contact opnemen met support',
      retryDownload: 'Download opnieuw proberen',
      downloadFailed:
        'Download mislukt. Controleer je verbinding en probeer het opnieuw.',
      linkCopied: 'Link gekopieerd. Je kunt hem in een bericht plakken.',
      shareFailed: 'Delen is momenteel niet beschikbaar. Probeer het opnieuw.',
    },
  },
  CustomerDigitalExtras: {
    downloadTitle: 'Je foto’s en video',
    downloadIntro: 'Bewaar je foto’s en video, of open je QR-ticket.',
    photosTitle: 'Je foto’s',
    photosIntro: 'Bewaar je foto’s, of open je QR-pas.',
    navigation: 'Je downloads',
    tabs: {
      photos: 'Foto’s',
      extras: 'Video',
      pass: 'QR-ticket',
    },
    title: 'Je digitale extra’s',
    loading: 'Je digitale extra’s laden…',
    loadError:
      'Bestanden laden mislukt. Controleer je verbinding en probeer opnieuw.',
    included: 'Inbegrepen',
    includedOrder:
      'Je video is inbegrepen. Bewaar je QR-ticket om de video later te openen.',
    preparingHelp:
      'Bewaar deze link of je QR-ticket. Je kunt deze pagina sluiten en later terugkomen.',
    reference: 'Referentie: {reference}',
    retry: 'Opnieuw proberen',
    empty: 'Er zijn nog geen digitale extra’s aan dit bezoek toegevoegd.',
    orderTitle: 'Videobestelling',
    showCashier: 'Toon deze bestelling en uw QR-ticket aan de kassa.',
    videoTotal: 'Totaal video',
    payAtDesk: 'Betaling aan de kassa.',
    preparing: 'Je bestanden worden voorbereid…',
    failed:
      'Je video kon niet worden gemaakt. Toon de onderstaande referentie aan een medewerker voor hulp.',
    ready: 'Klaar om te downloaden',
    video: 'Bewegende foto',
    videoDescription:
      'Je favoriete moment in beweging. Klaar voor je stories en de familiechat.',
    download: 'Downloaden',
    share: 'Bestand delen',
    preparingShare: 'Bestand laden…',
    shareFailed: 'Delen is niet beschikbaar. Gebruik Downloaden.',
  },
  GoPhotoUpload: {
    waitingForPortraits: 'Je portretten worden afgerond',
    waitingForPortraitsSubtitle:
      'Even geduld terwijl de overige portretten worden gemaakt.',
    success: "Foto's geüpload!",
    successSubtitle: "Je foto's zijn nu beschikbaar op je PhotoPass.",
  },
  system: {
    reconnecting: 'Verbinding herstellen…',
  },
} satisfies LocaleMessageSchema;
